// クレアショット個包装（35 x 130mm 版下）を、実物の袋に近い形と質感で作る
// 単位: mm で形を作り、最後に S 倍して three の単位にする
export async function buildStick(THREE, renderer, urls) {
  const MW = 35, MH = 130;          // 版下の寸法(mm)
  const SEAL = 9;                   // 上下の圧着シール(mm)
  const FIN = 7;                    // 背面の縦の貼り合わせ幅(mm)
  const T = 2.5;                    // 中央のふくらみ（片面, mm）
  const S = 0.62 / MW;              // mm → three 単位
  const NX = 56, NY = 300;

  // ---- 乱数・ノイズ（毎回同じ形になるよう固定シード）----
  let seed = 7;
  const rnd = () => ((seed = (seed * 16807) % 2147483647) / 2147483647);
  const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
  const bumps = Array.from({ length: 70 }, () => ({ x: rnd() * MW - MW / 2, y: rnd() * MH, r: 3 + rnd() * 10, a: (rnd() - 0.5) * 0.5 }));
  const lowNoise = (x, y) => { let h = 0; for (const b of bumps) { const d2 = ((x - b.x) ** 2 + (y - b.y) ** 2) / (b.r * b.r); if (d2 < 4) h += b.a * Math.exp(-d2); } return h; };

  // ---- 片面の高さ（mm）----
  function height(x, y, back) {
    const u = x / (MW / 2);
    const side = Math.sqrt(Math.max(0, 1 - Math.pow(Math.min(1, Math.abs(u)), 2.2)));
    const d = Math.min(y - SEAL, MH - SEAL - y);                    // シールからの距離
    const e = smooth(0, 13, d);                                     // シールで0
    const fill = 0.72 + 0.28 * smooth(MH * 0.88, MH * 0.25, y);     // 粉は下に寄る
    // シール際の放射状のしわ（ふくらみ始めで波打つ）
    const wz = Math.exp(-((d - 4) ** 2) / 8) * 0.16 * Math.sin(u * Math.PI * 9 + (y > MH / 2 ? 1.3 : 0)) * side;
    const n = lowNoise(x, y) * e * (back ? 0.8 : 1);
    return Math.max(0, T * side * e * fill + wz + n * side);
  }
  // ふくらんだ分だけ幅が縮む（シール部は元の幅のまま＝くびれ）
  const shrink = (y) => { const d = Math.min(y - SEAL, MH - SEAL - y); return 1 - 0.07 * smooth(0, 16, d); };

  function sheet(back) {
    const g = new THREE.PlaneGeometry(MW, MH, NX, NY);
    const p = g.attributes.position, uv = g.attributes.uv;
    for (let i = 0; i < p.count; i++) {
      const x0 = p.getX(i), y = p.getY(i) + MH / 2;
      const z = height(x0, y, back);
      p.setXYZ(i, x0 * shrink(y), y - MH / 2, back ? -z : z);
      if (back) uv.setX(i, 1 - uv.getX(i));
    }
    if (back) { const idx = g.index.array; for (let i = 0; i < idx.length; i += 3) { const t = idx[i]; idx[i] = idx[i + 2]; idx[i + 2] = t; } }
    g.scale(S, S, S);
    g.computeVertexNormals();
    return g;
  }

  // ---- 法線マップ・粗さマップ・切り口（キャンバスで生成）----
  const TW = 512, TH = Math.round(512 * MH / MW);
  const mmX = TW / MW, mmY = TH / MH;
  function makeMaps(back) {
    const h = new Float32Array(TW * TH);
    const rough = new Uint8ClampedArray(TW * TH * 4);
    for (let j = 0; j < TH; j++) {
      const y = MH - j / mmY;                       // 上が j=0
      const inSeal = y < SEAL || y > MH - SEAL;
      for (let i = 0; i < TW; i++) {
        const x = i / mmX - MW / 2;
        let v = 0;
        // フィルムの細かいよれ
        v += Math.sin(x * 0.53 - y * 0.21) * 0.05 + Math.sin(x * 0.21 + y * 0.37) * 0.04;   // ゆるいよれだけ（細かいよれはキラキラの原因）
        if (inSeal) v += Math.sin(y * 8.0) * 0.9 * Math.min(1, Math.min(y, MH - y) / 1.2); // 圧着の溝（横線・はっきり）
        if (back) {
          const fx = x + 1.5; // 貼り合わせは少し右寄りに倒れている
          if (Math.abs(fx) < FIN / 2) v += Math.sin(y * 9.0) > 0 ? 0.12 : -0.12; // 貼り合わせ部の圧着の溝
        }
        h[j * TW + i] = v;
        const r = inSeal ? 120 : 92 + Math.round(Math.abs(Math.sin(x * 0.17 + y * 0.05)) * 14);   // 艶のあるラミネートフィルム
        const o = (j * TW + i) * 4; rough[o] = rough[o + 1] = rough[o + 2] = r; rough[o + 3] = 255;
      }
    }
    const nrm = new Uint8ClampedArray(TW * TH * 4);
    for (let j = 0; j < TH; j++) for (let i = 0; i < TW; i++) {
      const l = h[j * TW + Math.max(0, i - 1)], r = h[j * TW + Math.min(TW - 1, i + 1)];
      const u = h[Math.max(0, j - 1) * TW + i], d = h[Math.min(TH - 1, j + 1) * TW + i];
      let nx = (l - r) * 2.2, ny = (d - u) * 2.2, nz = 1; const k = 1 / Math.hypot(nx, ny, nz);
      const o = (j * TW + i) * 4;
      nrm[o] = (nx * k * 0.5 + 0.5) * 255; nrm[o + 1] = (ny * k * 0.5 + 0.5) * 255; nrm[o + 2] = (nz * k * 0.5 + 0.5) * 255; nrm[o + 3] = 255;
    }
    const toTex = (data, srgb) => { const t = new THREE.DataTexture(data, TW, TH, THREE.RGBAFormat); t.flipY = true; t.needsUpdate = true; t.anisotropy = 8; t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace; t.generateMipmaps = true; t.minFilter = THREE.LinearMipmapLinearFilter; return t; };
    return { normal: toTex(nrm), rough: toTex(rough) };
  }
  // 上下の切り口のギザギザ
  const alpha = (() => {
    const c = document.createElement('canvas'); c.width = 256; c.height = 2048;
    const x = c.getContext('2d'); x.fillStyle = '#fff'; x.fillRect(0, 0, c.width, c.height);
    const py = c.height / MH, tooth = 1.6 * (c.width / MW), depth = 0.9 * py;
    x.fillStyle = '#000';
    for (const top of [true, false]) {
      x.beginPath();
      const y0 = top ? 0 : c.height;
      x.moveTo(0, y0);
      for (let i = 0; i <= c.width + tooth; i += tooth) {
        x.lineTo(i, y0); x.lineTo(i + tooth / 2, top ? y0 + depth : y0 - depth); x.lineTo(i + tooth, y0);
      }
      x.lineTo(c.width, y0); x.closePath(); x.fill();
    }
    const t = new THREE.CanvasTexture(c); return t;
  })();

  const loader = new THREE.TextureLoader();
  const load = (u) => new Promise((ok, ng) => loader.load(u, (t) => { t.colorSpace = THREE.SRGBColorSpace; t.anisotropy = renderer.capabilities.getMaxAnisotropy(); ok(t); }, undefined, ng));
  const [mapF, mapB] = await Promise.all([load(urls.front), load(urls.back)]);
  const mF = makeMaps(false), mB = makeMaps(true);

  const film = {
    roughness: 1, metalness: 0, clearcoat: 1, clearcoatRoughness: 0.17,
    normalScale: new THREE.Vector2(0.5, 0.5), alphaMap: alpha, alphaTest: 0.5,
    specularIntensity: 0.5, envMapIntensity: 1.0,
  };
  const matF = new THREE.MeshPhysicalMaterial({ map: mapF, normalMap: mF.normal, roughnessMap: mF.rough, clearcoatNormalMap: mF.normal, clearcoatNormalScale: new THREE.Vector2(0.2, 0.2), ...film });
  const matB = new THREE.MeshPhysicalMaterial({ map: mapB, normalMap: mB.normal, roughnessMap: mB.rough, clearcoatNormalMap: mB.normal, clearcoatNormalScale: new THREE.Vector2(0.2, 0.2), ...film });
  // 裏面の uv は左右反転しているので、法線マップの x も反転させる
  matB.normalScale.set(-0.35, 0.35); matB.clearcoatNormalScale.set(-0.2, 0.2);
  const gF = sheet(false), gB = sheet(true);

  // ---- 写り込み用のスタジオ（細長いソフトボックス）----
  const env = (() => {
    const pm = new THREE.PMREMGenerator(renderer), s = new THREE.Scene();
    s.add(new THREE.Mesh(new THREE.SphereGeometry(30, 32, 16), new THREE.MeshBasicMaterial({ color: 0x1c1d1c, side: THREE.BackSide })));
    const box = (w, h, x, y, z, c, k = 1) => { const m = new THREE.Mesh(new THREE.PlaneGeometry(w, h), new THREE.MeshBasicMaterial({ color: new THREE.Color(c).multiplyScalar(k), side: THREE.DoubleSide })); m.position.set(x, y, z); m.lookAt(0, 0, 0); s.add(m); };
    // 撮影スタジオの縦長ストリップ: ふくらみに沿って縦に長い光の帯が入る（写真らしさの一番の要素）
    box(2.2, 18, -4.2, 1, 8, 0xffffff, 4.2);   // 左の縁に光の帯
    box(2.2, 18, 4.2, 1, 8, 0xffffff, 3.2);    // 右の縁に光の帯
    box(9, 16, 0, 1, 12, 0xffffff, 0.55);      // 正面の大きな拡散光（色を明るく）
    box(10, 2.5, 0, 9, 1, 0xffffff, 0.9);
    box(7, 14, -6, 2, -7, 0xffffff, 1.1);    // 背面側にもソフトボックス（裏を向いた時の反射）
    box(10, 6, 0, -1, -10, 0x9fd65a, 0.25);    // 背後の緑の照り返し
    box(14, 2, 0, -8, 4, 0xffffff, 0.25);      // 下からの弱いレフ
    const t = pm.fromScene(s, 0.02).texture; pm.dispose(); return t;
  })();
  const key = new THREE.DirectionalLight(0xffffff, 1.5); key.position.set(-3, 4, 5);
  const rim = new THREE.DirectionalLight(0xf2ffe6, 0.9); rim.position.set(4, 2, -4);
  const fill = new THREE.DirectionalLight(0xffffff, 0.7); fill.position.set(2, 1, -6);
  const lights = [key, rim, fill];

  // ---- 背面の縦の貼り合わせ（フィン）: 背面に沿って倒れた細い帯 ----
  const gFin = (() => {
    const off = -1.5, x0 = off - FIN / 2, x1 = off + FIN / 2, ny = 300, lip = 0.28;
    const pos = [], uvs = [], idx = [];
    const cols = [[x0, 0], [x0, lip], [x1, lip], [x1, 0]]; // 断面: 立ち上がり→上面→立ち下がり
    for (let j = 0; j <= ny; j++) {
      const y = 0.4 + (MH - 0.8) * j / ny;
      for (const [x, l] of cols) {
        const z = -(height(x, y, true) + 0.05 + l);
        pos.push(x * shrink(y), y - MH / 2, z);
        uvs.push(1 - (x / MW + 0.5), y / MH);
      }
      if (j) for (let k = 0; k < 3; k++) { const a = (j - 1) * 4 + k, b = a + 4; idx.push(a, a + 1, b, a + 1, b + 1, b); }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
    g.setIndex(idx); g.scale(S, S, S); g.computeVertexNormals(); return g;
  })();
  const matFin = matB.clone(); matFin.color = new THREE.Color(0.9, 0.9, 0.9); matFin.side = THREE.DoubleSide;

  function makeStick() {
    const g = new THREE.Group();
    const a = new THREE.Mesh(gF, matF), b = new THREE.Mesh(gB, matB), f = new THREE.Mesh(gFin, matFin);
    a.castShadow = b.castShadow = true;
    g.add(a, b, f);
    return g;
  }
  return { env, lights, makeStick, W: MW * S, H: MH * S };
}
